import { IOMOffer, OfferTile, OfferTileCta, OfferTileIncentiveGroup } from '../../../models/iom-offers.model'
import { debounce } from '../../../utils/debounce'
import { sanitizeInput } from '../../../utils/sanitize'
import { GMXGoogleSearchAddress } from '../../google-search-address/google-search-address'
import { LOCATION_CHANGE_EVENT } from '../../../models/location.model'

/**
 * Block Carousel (For Offer Tiles & Cards)
 */
export class GMXBlockCarousel extends HTMLElement {
	headerEl: HTMLElement
	scrollerEl: HTMLElement
	controlsPaginationPrevEl: HTMLButtonElement
	controlsPaginationNextEl: HTMLButtonElement
	locationPromptPrefixEl: HTMLElement
	locationPickerBtn: HTMLButtonElement
	locationPanel: HTMLElement
	locationPanelSearchAddressEl: GMXGoogleSearchAddress

	private boundControlsPrevBtnClickHandler = this._handleControlsPrevBtnClick.bind(this)
	private boundControlsNextBtnClickHandler = this._handleControlsNextBtnClick.bind(this)
	private boundLocationPanelSearchHandler = this._handleLocationPanelSearch.bind(this)
	private boundLocationChangeHandler = this._handleLocationChange.bind(this)

	private listeningForLocation = false

	// Identifies the newest offers request so slower, superseded ones cannot render.
	private offerRequestId = 0

	private offers: IOMOffer[]

	// Zip of the most recent fetch, used only as the fallback for direct calls to the
	// public `addIOMOffersToCarousel`.  The internal render path passes its zip explicitly.
	private currentZip: string

	private carouselGap = 0

	private resizeObserver: ResizeObserver

	get iomOffersEndpoint(): string {
		return this.getAttribute('data-iom-offers-endpoint')
	}
	set iomOffersEndpoint(val: string) {
		this.setAttribute('data-iom-offers-endpoint', val)
	}

	get iomOffersBodystyle(): string {
		return this.getAttribute('data-iom-offers-bodystyle')
	}
	set iomOffersBodystyle(val: string) {
		this.setAttribute('data-iom-offers-bodystyle', val)
	}

	get labelData(): Record<string, string> {
		return this.hasAttribute('data-labels') ? JSON.parse(this.getAttribute('data-labels')) : {}
	}
	set labelData(val: Record<string, string>) {
		this.setAttribute('data-labels', JSON.stringify(val))
	}

	get brand(): string {
		return this.getAttribute('data-brand')
	}
	set brand(val: string) {
		this.setAttribute('data-brand', val)
	}

	async connectedCallback(): Promise<void> {
		this.headerEl = this.querySelector('gmx-headline')
		this.scrollerEl = this.querySelector('.block-carousel-scroller')
		this.controlsPaginationPrevEl = this.querySelector(
			'.gmx-controls-container button.gmx-prev-ctrl'
		) as HTMLButtonElement
		this.controlsPaginationNextEl = this.querySelector(
			'.gmx-controls-container button.gmx-next-ctrl'
		) as HTMLButtonElement
		this.locationPromptPrefixEl = this.querySelector('span.heading-location-prompt-prefix') as HTMLElement
		this.locationPickerBtn = this.querySelector('button.heading-location-picker') as HTMLButtonElement

		if (this.locationPickerBtn) {
			this.locationPanel = document.getElementById(this.locationPickerBtn.getAttribute('data-panel-reveal-id'))
			if (this.locationPanel) {
				this.locationPanelSearchAddressEl = this.locationPanel.querySelector('gmx-google-search-address')
				this.locationPanelSearchAddressEl.addEventListener(
					'gmxGoogleAutocompleteSearch',
					this.boundLocationPanelSearchHandler
				)
			}
		}

		if (this.controlsPaginationPrevEl) {
			this.controlsPaginationPrevEl.addEventListener('click', this.boundControlsPrevBtnClickHandler)
		}

		if (this.controlsPaginationNextEl) {
			this.controlsPaginationNextEl.addEventListener('click', this.boundControlsNextBtnClickHandler)
		}

		if (this.iomOffersEndpoint) {
			const location = await window.gmxLocationSingleton.resolve()

			// Removal during the await already ran disconnectedCallback, so listening now
			// would attach a handler nothing is left to tear down.
			if (!this.isConnected) {
				return
			}

			// Attached after the initial resolve, so the akamai value it may write on a cold
			// load does not trigger a second, duplicate fetch, but before the first render,
			// so a zip changed while those offers are still loading is not missed.
			window.addEventListener(LOCATION_CHANGE_EVENT, this.boundLocationChangeHandler)
			this.listeningForLocation = true

			if (location?.zip) {
				this.locationPromptPrefixEl.innerText = this.labelData['i18n_offersFoundPrefix'] || 'Label missing'

				await this._renderOffersForZip(location.zip)
			} else {
				this._showLocationPrompt()
			}
		}

		this._handleComponentResize()

		this.resizeObserver = new ResizeObserver(debounce(this._handleComponentResize.bind(this), 250))
		this.resizeObserver.observe(this.scrollerEl)
	}

	disconnectedCallback(): void {
		if (this.controlsPaginationPrevEl) {
			this.controlsPaginationPrevEl.removeEventListener('click', this.boundControlsPrevBtnClickHandler)
		}

		if (this.controlsPaginationNextEl) {
			this.controlsPaginationNextEl.removeEventListener('click', this.boundControlsNextBtnClickHandler)
		}

		if (this.locationPanel) {
			this.locationPanelSearchAddressEl.removeEventListener(
				'gmxGoogleAutocompleteSearch',
				this.boundLocationPanelSearchHandler
			)
		}

		if (this.listeningForLocation) {
			window.removeEventListener(LOCATION_CHANGE_EVENT, this.boundLocationChangeHandler)
			this.listeningForLocation = false
		}

		if (this.resizeObserver) {
			this.resizeObserver.unobserve(this.scrollerEl)
		}
	}

	/**
	 * The panel only records the zip; rendering happens in the location change handler so
	 * that a zip entered here and one set by another app on the page take the same path.
	 * @private
	 */
	private _handleLocationPanelSearch(e: CustomEvent): void {
		window.gmxLocationSingleton.setUserZip(e.detail.searchStr)
		this.locationPanel.removeAttribute('data-show')
	}

	/**
	 * Re-render whenever the shared location changes, whoever changed it.
	 * @private
	 */
	private async _handleLocationChange(e: WindowEventMap['gmx-location-change']): Promise<void> {
		if (!this.isConnected) {
			return
		}

		const zip = e.detail.location?.zip

		// The tier can change while the zip does not — a geo-IP guess confirmed by the
		// signed-in profile, say — and the offers for it would be identical.
		if (zip && zip === e.detail.previous?.zip) {
			return
		}

		if (zip) {
			await this._renderOffersForZip(zip)
		} else {
			this._showLocationPrompt()
		}
	}

	/**
	 * Fetch and render the offers for a zip.  `addIOMOffersToCarousel` clears previously
	 * rendered offers first, so this is safe to call repeatedly.
	 * @private
	 */
	private async _renderOffersForZip(zip: string): Promise<void> {
		const requestId = ++this.offerRequestId
		const offers = await this._fetchIOMOffers(zip, requestId)

		if (!this._isCurrentRequest(requestId)) {
			return
		}

		this.offers = offers

		if (this.offers) {
			this.addIOMOffersToCarousel(this.offers, zip)
		}
	}

	/**
	 * Zips can change faster than the endpoint answers, and responses need not arrive in
	 * the order they were asked for, so a superseded request must not reach the DOM.
	 * @private
	 */
	private _isCurrentRequest(requestId: number): boolean {
		return requestId === this.offerRequestId && this.isConnected
	}

	/**
	 * Headline state for when no location is known yet.  Losing the location is a state
	 * change like any other zip change, so offers still in flight for the zip that was
	 * just dropped are superseded here rather than being allowed to paint over the prompt.
	 * @private
	 */
	private _showLocationPrompt(): void {
		this.offerRequestId++
		this.locationPromptPrefixEl.innerText =
			this.labelData['i18n_offersLocationNotSearchedPrefix'] || 'Label missing'
		this.locationPickerBtn.innerText = this.labelData['i18n_offersLocationPrompt'] || 'Label missing'
	}

	/**
	 * Fetch IOM offers from defined endpoint.
	 * @param url
	 * @private
	 */
	private async _fetchIOMOffers(zip: string, requestId: number): Promise<IOMOffer[]> {
		this.currentZip = zip

		try {
			// The configured endpoint may or may not carry a trailing slash, so it is trimmed
			// off before the zip is appended.
			const endpoint = this.iomOffersEndpoint.replace(/\/+$/, '')
			const response = await fetch(
				`${endpoint}/${encodeURIComponent(zip)}?make=${encodeURIComponent(this.brand || '')}`
			)

			if (!response.ok) {
				throw new Error(`Response NOT ok: ${response.status}`)
			}

			const offers = await response.json()

			if (this._isCurrentRequest(requestId)) {
				if (offers.count === 0) {
					this.locationPromptPrefixEl.innerText =
						this.labelData['i18n_offersNotFoundPrefix'] || 'Label missing'
					this.locationPickerBtn.innerText = zip
				} else {
					this.locationPromptPrefixEl.innerText = this.labelData['i18n_offersFoundPrefix'] || 'Label missing'
					this.locationPickerBtn.innerText = zip
				}
			}

			return offers.results || ([] as IOMOffer[])
		} catch (error) {
			window.console.error(`ERROR:  Failed to fetch Offers: ${error}`)

			return []
		}
	}

	/**
	 * Callback for mobile carousel previous button click handle.
	 *
	 * @private
	 */
	private _handleControlsPrevBtnClick(): void {
		const atBeginning = this.scrollerEl.scrollLeft <= 1
		const scrollBy = -(this.scrollerEl.querySelector('li') as HTMLElement).offsetWidth - this.carouselGap
		const newScrollLeft = this.scrollerEl.scrollLeft + scrollBy

		this.scrollerEl.scrollTo({
			left: atBeginning ? this.scrollerEl.scrollWidth : Math.max(0, newScrollLeft),
			behavior: 'smooth',
		})
	}

	/**
	 * Callback for mobile carousel next button click handle.
	 *
	 * @private
	 */
	private _handleControlsNextBtnClick(): void {
		const atEnd = this.scrollerEl.scrollWidth - this.scrollerEl.scrollLeft - this.scrollerEl.clientWidth <= 1
		const scrollBy = (this.scrollerEl.querySelector('li') as HTMLElement).offsetWidth + this.carouselGap
		const newScrollLeft = this.scrollerEl.scrollLeft + scrollBy

		this.scrollerEl.scrollTo({
			left: atEnd ? 0 : newScrollLeft,
			behavior: 'smooth',
		})
	}

	/**
	 * Callback for handling window resize.  Need to make accessible or not.
	 * @private
	 */
	private _handleComponentResize(): void {
		const carouselGap = getComputedStyle(this.scrollerEl).getPropertyValue('column-gap')

		if (carouselGap.match(/px$/)) {
			this.carouselGap = parseInt(carouselGap.replace('px', ''))
		} else {
			this.carouselGap = 0
		}

		if (this.scrollerHasOverflow()) {
			this.headerEl.dataset.paginationEnabled = 'true'
			this.scrollerEl.setAttribute('tabIndex', '0')
		} else {
			this.headerEl.dataset.paginationEnabled = 'false'
			this.scrollerEl.removeAttribute('tabIndex')
		}
	}

	/**
	 * Re-evaluate the pagination controls after a wrapping component changes the list items.
	 */
	refreshPagination(): void {
		// A wrapper connects before this element does, and connectedCallback runs its own check.
		if (!this.scrollerEl) {
			return
		}

		this._handleComponentResize()
	}

	/**
	 * Check if the screen is mobile size
	 */
	scrollerHasOverflow(): boolean {
		return this.scrollerEl.scrollWidth > this.scrollerEl.clientWidth
	}

	addIOMOffersToCarousel(offers: IOMOffer[], zip: string = this.currentZip): void {
		this.scrollerEl.querySelectorAll('li').forEach(offerListItem => {
			if (offerListItem.classList.contains('iom-offer')) {
				this.scrollerEl.removeChild(offerListItem)
			}
		})

		offers.forEach(o => {
			if (o.bodystyle === this.iomOffersBodystyle && o.offerTile && !this._isFeaturedOffer(o)) {
				const offerListEl = document.createElement('li')
				offerListEl.classList.add('iom-offer')

				offerListEl.innerHTML = `
				<gmx-offers-tile>
					${this._createOfferTileDetailsMrkup(o, zip)}
				</gmx-offers-tile>`

				this.scrollerEl.appendChild(offerListEl)
			}
		})

		// Manually trigger resize to update pagination controls
		this._handleComponentResize()
	}

	/**
	 * Featured offers are authored in the CMS and pre-rendered into the carousel, so the
	 * ones the offers service returns are skipped to avoid rendering them twice.
	 * @param offer
	 * @private
	 */
	private _isFeaturedOffer(offer: IOMOffer): boolean {
		return offer.offerType?.toLowerCase() === 'featured' || offer.offerTile?.offerType?.toLowerCase() === 'featured'
	}

	/**
	 * Create Offer Tile Internal Template from the offer's `offerTile` payload.
	 * @param offer
	 * @param zip  The zip the offers were fetched with, carried through so overlapping
	 *             searches cannot pair one search's offers with another's zip.
	 * @private
	 */
	private _createOfferTileDetailsMrkup(offer: IOMOffer, zip: string): string {
		const offerTile = offer.offerTile
		const offerType = offerTile.offerType || ''
		const ymmStr = offerTile.subtitle || ''
		const imageAlt = `${offer.modelYear} ${offer.displayName}`
		const firstCTA = offerTile.ctaList?.length ? offerTile.ctaList[0] : null
		const thirdCTA = offerTile.ctaList?.length > 2 ? offerTile.ctaList[2] : null
		const incentiveGroups = offerTile.incentiveGroups || []

		const offerMrkup =
			offerTile.ctaList?.length && firstCTA?.htmlcomponent
				? `<div class="offers-flex-container">
				<div class="offers-info">
					<div class="tile-header">
						<div class="offer-type">${offerType}</div>
					</div>
					<div class="tile-ymm">${ymmStr}</div>
					<div class="body">
						${incentiveGroups.map((group, i) => this._createIncentiveGroupMrkup(offerTile, group, i)).join('')}
					</div>
					${thirdCTA ? this._createOfferSpecialtyLinkMrkup(thirdCTA, offer, zip) : ''}
				</div>
				${
					offerTile.image
						? `<div class="offers-image"><img src="${offerTile.image}" alt="${imageAlt}" aria-label="${imageAlt}"/></div>`
						: ''
				}
				<div class="offers-cta-container">
					${offerTile.ctaList
						.slice(0, 2)
						.map(cta => this._createOfferTileCtaMrkup(offer, cta, zip))
						.join('')}
				</div>
			</div>
		`
				: `<div class="offers-flex-container">
				<div class="offers-info">
					<div class="tile-header">
						<span class="gmx-tag">${offerType}</span>
						${firstCTA ? this._createOfferTileCtaMrkupDeprecated(offer, firstCTA, zip) : ''}
					</div>
					<div class="tile-ymm">${ymmStr}</div>
					<div class="body">
						${incentiveGroups.map((group, i) => this._createIncentiveGroupMrkup(offerTile, group, i)).join('')}
					</div>
				</div>
				${
					offerTile.image
						? `<div class="offers-image"><img src="${offerTile.image}" alt="${imageAlt}" aria-label="${imageAlt}"/></div>`
						: ''
				}
			</div>
		`

		return window.gmxDOMPurify && typeof window.gmxDOMPurify.sanitize === 'function'
			? window.gmxDOMPurify.sanitize(offerMrkup)
			: offerMrkup
	}

	/**
	 * Create the markup for a single incentive group.  Offers can carry more than one
	 * group, in which case each group after the first is separated by a rule and the
	 * tile's modifier text.
	 * @param offerTile
	 * @param group
	 * @param index
	 * @private
	 */
	private _createIncentiveGroupMrkup(offerTile: OfferTile, group: OfferTileIncentiveGroup, index: number): string {
		// Legal copy covers the whole tile, so it hangs off the first group's title where
		// the toast singleton turns it into a disclosure trigger.
		const legalAttr =
			index === 0 && offerTile.legalText ? ` data-legal-msg="${sanitizeInput(offerTile.legalText)}"` : ''

		return `
			${index > 0 ? '<hr/>' : ''}
			${index > 0 && offerTile.modifierText ? `<div class="offer-qualifier">${offerTile.modifierText}</div>` : ''}
			<div class="offer-title"${legalAttr}>${group.header || ''}</div>
			${group.description ? `<div class="offer-subtitle">${group.description}</div>` : ''}
			${
				group.details?.length
					? `<ul class="offer-details">${group.details.map(detail => `<li>${detail}</li>`).join('')}</ul>`
					: ''
			}
		`
	}

	/**
	 * Create markup for the third offer CTA, rendered as a specialty link.
	 * @param cta
	 * @param offer
	 * @param zip
	 * @private
	 */
	private _createOfferSpecialtyLinkMrkup(cta: OfferTileCta, offer: IOMOffer, zip: string): string {
		const ctaLinks = offer.ctaLinks || {}
		const offerModelYear = offer.modelYear || ''
		const offerBodystyle = offer.bodystyle || ''

		if (cta.type === 'panel') {
			return `
				<button
					class="gmx-btn gmx-btn-link-primary icon-right offer-specialty-link"
					data-icon-name="arrow"
					data-icon-size="16"
					data-panel-reveal-id="${cta.url}"
					data-panel-type="form-panel"
					data-panel-data='{"vehicleSelectorYear":"${offerModelYear}","vehicleSelectorModel":"${offerBodystyle}"}'
				>${cta.text}</button>
			`
		} else {
			const offerBaseUrl = cta.ctaLinkId && ctaLinks[cta.ctaLinkId] ? ctaLinks[cta.ctaLinkId] : ''

			if (offerBaseUrl) {
				return `
				<a href="${this._buildCtaHref(offerBaseUrl, offer, cta, zip)}" class="gmx-btn gmx-btn-link-primary icon-right offer-specialty-link" data-icon-name="arrow" data-icon-size="16">${cta.text}</a>
			`
			} else {
				window.console.log(`Warning: No matching specialty link ctaLink found for ctaLinkId "${cta.ctaLinkId}"`)
			}
		}
	}

	/**
	 * Create the tile header CTA from the first entry in the offer tile's CTA list.
	 * @param offer
	 * @param cta
	 * @param zip
	 * @private
	 */
	private _createOfferTileCtaMrkup(offer: IOMOffer, cta: OfferTileCta, zip: string): string {
		const offerModelYear = offer.modelYear || ''
		const offerBodystyle = offer.bodystyle || ''
		const ctaLinks = offer.ctaLinks || {}

		if (cta.type === 'panel') {
			return `
				<button
					class="gmx-btn gmx-btn-tertiary grow-on-max"
					data-icon-name="arrow"
					data-icon-size="16"
					data-panel-reveal-id="${cta.url}"
					data-panel-type="form-panel"
					data-panel-data='{"vehicleSelectorYear":"${offerModelYear}","vehicleSelectorModel":"${offerBodystyle}"}'
				>${cta.text}</button>
			`
		} else {
			const offerBaseUrl = cta.ctaLinkId && ctaLinks[cta.ctaLinkId] ? ctaLinks[cta.ctaLinkId] : ''

			if (offerBaseUrl) {
				return `
				<a href="${this._buildCtaHref(offerBaseUrl, offer, cta, zip)}" class="gmx-btn gmx-btn-tertiary grow-on-max">${cta.text}</a>
			`
			} else {
				window.console.log(`Warning: No matching link ctaLink found for ctaLinkId "${cta.ctaLinkId}"`)
			}
		}
	}

	/**
	 * CTA urls arrive without a query string, so the shopping params inventory search
	 * expects are appended here.  Some urls are relative AEM paths, so the query string
	 * is concatenated rather than built with `URL`.
	 * @param offer
	 * @param cta
	 * @param zip
	 * @private
	 */
	private _buildCtaHref(offerBaseUrl: string, offer: IOMOffer, cta: OfferTileCta, zip: string): string {
		const url = offerBaseUrl

		if (!url) {
			return ''
		}

		const params: string[] = []

		if (zip) {
			params.push(`zipCode=${encodeURIComponent(zip)}`)
		}

		if (offer.offerType) {
			params.push(`paymentType=${encodeURIComponent(offer.offerType.toUpperCase())}`)
		}

		if (offer.programIDs && cta.ctaLinkId === 'drpshopper') {
			params.push(`leaseOffers=${encodeURIComponent(offer.programIDs)}`)
		}

		if (!params.length) {
			return url
		}

		return `${url}${url.includes('?') ? '&' : '?'}${params.join('&')}`
	}

	/**
	 * Create the tile header CTA from the first entry in the offer tile's CTA list.
	 * @param offer
	 * @param cta
	 * @param zip
	 * @private
	 */
	private _createOfferTileCtaMrkupDeprecated(offer: IOMOffer, cta: OfferTileCta, zip: string): string {
		const ctaText = cta.text || ''
		const idAttr = offer.programIDs ? ` id="header-cta-${offer.programIDs}"` : ''
		const iconAttr = cta.icon ? ` data-icon-name="${cta.icon}"` : ''

		return `<a${idAttr} aria-label="${ctaText}" class="gmx-btn gmx-btn-link-primary icon-right" href="${this._buildCtaHrefDeprecated(offer, cta, zip)}"${iconAttr}>${ctaText}</a>`
	}

	/**
	 * CTA urls arrive without a query string, so the shopping params inventory search
	 * expects are appended here.  Some urls are relative AEM paths, so the query string
	 * is concatenated rather than built with `URL`.
	 * @param offer
	 * @param cta
	 * @param zip
	 * @private
	 */
	private _buildCtaHrefDeprecated(offer: IOMOffer, cta: OfferTileCta, zip: string): string {
		const url = cta.url || ''

		if (!url) {
			return ''
		}

		const params: string[] = []

		if (zip) {
			params.push(`zipCode=${encodeURIComponent(zip)}`)
		}

		if (offer.offerType) {
			params.push(`paymentType=${encodeURIComponent(offer.offerType.toUpperCase())}`)
		}

		if (offer.programIDs) {
			params.push(`leaseOffers=${encodeURIComponent(offer.programIDs)}`)
		}

		if (!params.length) {
			return url
		}

		return `${url}${url.includes('?') ? '&' : '?'}${params.join('&')}`
	}
}

if (!customElements.get('gmx-block-carousel')) {
	customElements.define('gmx-block-carousel', GMXBlockCarousel)
}
